import { AttachmentsSchema, attachmentsSchema } from './attachmentsSchema';
import {
  Campaign,
  CampaignsResponseSchema,
  campaignSchema,
  campaignsResponseSchema,
} from './campaignSchema';
import { rootApi } from '@components/rootApi';
import {
  campaignSettingResponse,
  CampaignSettingResponse,
  campaignSettingsResponse,
  CampaignSettingsResponse,
} from './campaignSettingsSchema';
import { CAMPAIGN_SETTING_TAG } from './tags';

export type GetCampaignsParams = Partial<{
  search_text: string;
  page: number;
  per_page: number;
  show_archived: boolean;
  account_manager_id: number;
  pod_id: number;
  manager_filter: string;
  report_date: string;
  include_unapproved: boolean;
}>;

export type GetCampaignParams = {
  id: number;
  custom_report_id?: string | number;
  include_unapproved?: boolean;
};

export type UpdateCampaignParams = {
  getCampaignsParams: GetCampaignsParams;
} & Pick<Campaign, 'id'> &
  Partial<Pick<Campaign, 'archived'>>;

const pathWithBaseUrl = (path: string) => `/api/v3/campaigns/${path}`;

export const campaignsApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getCampaignV3: builder.query<Campaign, GetCampaignParams>({
      query: ({ id, ...params }) => ({
        url: pathWithBaseUrl(id.toString()),
        params,
      }),
      transformResponse: (response: Campaign) => campaignSchema.parse(response),
    }),

    getCampaigns: builder.query<CampaignsResponseSchema, GetCampaignsParams>({
      query: params => ({ url: pathWithBaseUrl(''), params }),
      transformResponse: (response: CampaignsResponseSchema) =>
        campaignsResponseSchema.parse(response),
    }),

    updateCampaign: builder.mutation<Campaign, UpdateCampaignParams>({
      query: ({ id, getCampaignsParams: _sideEffectParams, ...body }) => ({
        url: pathWithBaseUrl(id.toString()),
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const result = dispatch(
          campaignsApi.util.updateQueryData(
            'getCampaigns',
            params.getCampaignsParams,
            draft => {
              const campaignIndex = draft.campaigns.findIndex(
                c => c.id === params.id
              );
              const campaign = draft.campaigns[campaignIndex];

              if (!campaign) {
                console.warn('Campaign not found in cache');
                return;
              }

              if (params.archived !== undefined)
                campaign.archived = params.archived;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          result.undo();
        }
      },
    }),

    getReportDatesV3: builder.query<string[], void>({
      query: () => pathWithBaseUrl('report_dates'),
    }),

    getAttachments: builder.query<AttachmentsSchema, number>({
      query: campaignId => pathWithBaseUrl(`${campaignId}/attachments`),
      transformResponse: (response: unknown) =>
        attachmentsSchema.parse(response),
    }),

    getCampaignSettings: builder.query<CampaignSettingsResponse, number>({
      query: campaignId => pathWithBaseUrl(`${campaignId}/campaign_settings`),
      transformResponse: (response: CampaignSettingsResponse) =>
        campaignSettingsResponse.parse(response),
      providesTags: result =>
        result && result.data.campaignSettings.length > 0
          ? result.data.campaignSettings.map(({ id }) => ({
              type: CAMPAIGN_SETTING_TAG,
              id,
            }))
          : [CAMPAIGN_SETTING_TAG],
    }),

    updateCampaignSettings: builder.mutation<
      CampaignSettingResponse,
      { campaignId: number; id: number; adSpend: number }
    >({
      query: ({ campaignId, id, ...body }) => ({
        url: pathWithBaseUrl(`${campaignId}/campaign_settings/${id}`),
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: CampaignSettingResponse) =>
        campaignSettingResponse.parse(response),
      invalidatesTags: result =>
        result
          ? [{ type: CAMPAIGN_SETTING_TAG, id: result.data.campaignSetting.id }]
          : [],
    }),

    createCampaignSettings: builder.mutation<
      CampaignSettingResponse,
      { campaignId: number; adSpend: number; partner: string | null }
    >({
      query: ({ campaignId, ...body }) => ({
        url: pathWithBaseUrl(`${campaignId}/campaign_settings`),
        method: 'POST',
        body,
      }),
      transformResponse: (response: CampaignSettingResponse) =>
        campaignSettingResponse.parse(response),
      invalidatesTags: [CAMPAIGN_SETTING_TAG],
    }),
  }),
});

export const {
  useGetCampaignV3Query,
  useGetCampaignsQuery,
  useUpdateCampaignMutation,
  useGetReportDatesV3Query,
  useGetAttachmentsQuery,
  useGetCampaignSettingsQuery,
  useUpdateCampaignSettingsMutation,
  useCreateCampaignSettingsMutation,
} = campaignsApi;
